/**
 * IMPORTANT
 * This file is shared between both Vue 2 and Vue 3 applications.
 *
 * Guidelines:
 * - Ensure this file remains framework-agnostic.
 * - Avoid using any framework-specific code (e.g., store, router).
 *
 * Purpose:
 * - Serves as a base class for `EpsilonTaggingManager.js` in both Vue 2 and Vue 3.
 * - Methods defined here will be accessible to both versions.
 *
 * For further details, please contact Leela directly.
 *
 */

import { sha256 } from 'js-sha256';

export class EpsilonTaggingManagerBase {

  getPageVisitParams(data) {
    return {
      dtm_cid: 3241,
      dtm_fid: 106,
      dtm_user_id: window.isauthenticated === 'True' ? window.userDetails.consumerid : '',
      dtm_email_hash:
        data.email || window.userDetails.email
          ? sha256(data.email || window.userDetails.email)
          : '',
      dealer_id: window.oemCode?.slice(-4),
      dealer_name: window.clientName,
      vendor: 'team_velocity',
      dtmc_loc: encodeURIComponent(window.location.href),
      cachebuster: new Date().valueOf(),
      dtm_cmagic: 'a64bd5',
      ...this.getBreadcrumbs(),
      ...data,
    };
  }

  getActionConversionParams(data) {
    return {
      dtm_cid: 3241,
      dtm_cmagic: 'a64bd5',
      dtm_fid: 107,
      dtm_user_id: window.isauthenticated === 'True' ? window.userDetails.consumerid : '',
      dtm_email_hash:
        data.email || window.userDetails.email
          ? sha256(data.email || window.userDetails.email)
          : '',
      vendor: 'team_velocity',
      dtmc_loc: encodeURIComponent(window.location.href),
      cachebuster: new Date().valueOf(),
      ...data,
    };
  }
  getSearchParamsFromObject(params) {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        searchParams.append(key, params[key]);
      }
    });
    return searchParams.toString();
  }

  addScriptToDOM(params) {
    const baseURL = 'https://login.dotomi.com/profile/visit/js/1_0?';
    const scriptURL = baseURL + this.getSearchParamsFromObject(params);
    const element = document.createElement('script');
    element.src = scriptURL;
    document.body.appendChild(element);
  }

  sendEvent(promoId, isPageVisit = true, additionalParams = {}) {
    let params = isPageVisit
      ? this.getPageVisitParams({
          dtm_promo_id: promoId,
          ...additionalParams,
        })
      : this.getActionConversionParams({
          dtm_promo_id: promoId,
          ...additionalParams,
        });
    this.addScriptToDOM(params);
  }
  firePageViews(pageType, uniquePageName) {
    let pageVisitTpe = uniquePageName || pageType;
    switch (pageVisitTpe) {
      case 'home':
        this.sendEvent(201);
        break;
      case 'service':
      case 'scheduleservicepage':
        this.sendEvent(205);
        break;
      case 'showroom':
      case 'coupon-specials':
      case 'researchModelsPage':
        this.sendEvent(203);
        break;
      case 'value_your_trade':
      case 'sellusyourcar':
        this.sendEvent(204);
        break;
      case 'newspecials':
      case 'preownedspecials':
      case 'incentivespage':
        this.sendEvent(208, false);
        break;
      case 'reserve-your-vehicle':
      case 'dealerspecials':
        this.sendEvent(204);
        break;
      case 'shoftpull':
      case 'getfinancingpage':
        this.sendEvent(202);
        break;
    }
  }

  fireCustomPageViews() {
    this.fireDealerOfferTags();
  }

  async getVehicleDetailPageView() {
    let vin = window.vin || '';
    const vehicleDetailsObject = await this.getVehicleDetails(vin);
    this.sendEvent(204, true, vehicleDetailsObject);
  }

  getBreadcrumbs() {
    const url = window.location.href;
    const urlObject = new URL(url);
    let pathValues = urlObject.pathname.split('/').filter((val) => val !== '');
    let breadcrumbsObj = {};
    for (let i = 0; i < pathValues.length; i++) {
      let key = 'breadcrumb_' + (i + 1);
      let value = pathValues[i];
      breadcrumbsObj[key] = value;
    }
    return breadcrumbsObj;
  }
  fireSRPPageView() {
    if (window.inventoryType !== 'new') {
      this.sendEvent(202);
    } else {
      this.sendEvent(207, false);
    }
  }

  fireDealerOfferTags() {
    let pageInfoForFiringScript = this.pageMappings().find((pageData) => {
      return pageData.pages.includes(window.location.pathname);
    });
    this.sendEvent(
      pageInfoForFiringScript?.promoId,
      pageInfoForFiringScript?.isPageVisitScriptType
    );
  }

  pageMappings() {
    return [
      {
        pages: ['/certified-by-volvo'],
        promoId: 202,
        isPageVisitScriptType: true,
      },
      {
        pages: [
          '/volvo-subscription-ny-ca',
          '/ex90-electric',
          '/ex30-electric',
          '/volvo-charging-partners',
          '/volvo-electric',
          '/sustainability',
          '/volvo-valet',
          '/volvo-car-livery-program',
          '/protection-plans',
          '/volvo-accessories',
          '/volvo-genuine-parts',
          '/certified-collision-centers',
          '/volvo-electrification',
        ],
        promoId: 203,
        isPageVisitScriptType: true,
      },
      {
        pages: ['/volvo-programs'],
        promoId: 204,
        isPageVisitScriptType: true,
      },
      {
        pages: ['/volvo-subscription'],
        promoId: 208,
        isPageVisitScriptType: false,
      },
    ];
  }
}
